/** Generates a winding board-game trail path and waypoint positions */

export interface WaypointPosition {
  x: number;
  y: number;
  index: number;
}

export interface TrailData {
  svgPath: string;
  waypoints: WaypointPosition[];
  viewBox: { width: number; height: number };
}

/**
 * Generate a serpentine (switchback) trail that winds left-to-right then right-to-left.
 * Returns SVG path data and waypoint coordinates.
 */
export function generateTrailPath(
  waypointCount: number,
  width = 280,
  rowHeight = 72,
  padding = 40,
): TrailData {
  const innerWidth = width - padding * 2;
  const rows = Math.ceil(waypointCount / 2);
  const height = rows * rowHeight + padding * 2;

  const waypoints: WaypointPosition[] = [];
  const pathParts: string[] = [];

  for (let i = 0; i < waypointCount; i++) {
    const row = Math.floor(i / 2);
    const isEvenRow = row % 2 === 0;
    const isLeft = i % 2 === 0;

    let x: number;
    if (isLeft) {
      x = isEvenRow ? padding : padding + innerWidth;
    } else {
      x = isEvenRow ? padding + innerWidth : padding;
    }

    const y = padding + row * rowHeight + (isLeft ? 0 : rowHeight / 2);

    waypoints.push({ x, y, index: i });

    if (i === 0) {
      pathParts.push(`M ${x} ${y}`);
    } else {
      const prev = waypoints[i - 1];
      const cpx1 = prev.x + (x - prev.x) * 0.5;
      const cpy1 = prev.y;
      const cpx2 = prev.x + (x - prev.x) * 0.5;
      const cpy2 = y;
      pathParts.push(`C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${x} ${y}`);
    }
  }

  return {
    svgPath: pathParts.join(' '),
    waypoints,
    viewBox: { width, height },
  };
}
